<template>
  <form @submit.prevent="handleSubmit" class="translations-form">
    <!-- Edit mode: read-only context + the editable text of ONE language -->
    <template v-if="isEditMode">
      <div class="form-group">
        <label class="form-label">{{ t('Página') }}</label>
        <div class="form-readonly" data-testid="translations-form-edit-page">{{ item?.pagina }}</div>
      </div>

      <div class="form-group">
        <label class="form-label">{{ t('Idioma') }}</label>
        <div class="form-readonly" data-testid="translations-form-edit-language">{{ idioma }}</div>
      </div>

      <div class="form-group">
        <label class="form-label">{{ t('Valor padrão') }}</label>
        <div class="form-readonly" data-testid="translations-form-edit-default-val">{{ item?.chave }}</div>
      </div>

      <div class="form-group">
        <label :for="`form-val-${uid}`" class="form-label">{{ t('Tradução') }}</label>
        <input-text
          :id="`form-val-${uid}`"
          v-model="formData.texto"
          :placeholder="t('Digite a tradução')"
          elem-id="translations_texto"
          data-testid="translations-form-edit-val"
        />
        <span v-if="errors.texto" class="error-text" data-testid="translations-form-error">
          {{ errors.texto }}
        </span>
      </div>
    </template>

    <!-- Create mode: register a key on an EXISTING page -->
    <template v-else>
      <div class="form-group">
        <label :for="`form-page-${uid}`" class="form-label">{{ t('Página') }} *</label>
        <select-list
          :id="`form-page-${uid}`"
          v-model="formData.pagina"
          elem-id="translations_pagina"
          :itens-select="pageOptions"
          :placeholder="t('Selecionar página')"
          data-testid="translations-form-page"
        />
        <span v-if="errors.pagina" class="error-text" data-testid="translations-form-page-error">
          {{ errors.pagina }}
        </span>
      </div>

      <div class="form-group">
        <label :for="`form-default-${uid}`" class="form-label">{{ t('Valor padrão') }} *</label>
        <input-text
          :id="`form-default-${uid}`"
          v-model="formData.chave"
          :required="true"
          :placeholder="t('Digite o valor padrão')"
          elem-id="translations_chave"
          data-testid="translations-form-default-val"
          @blur="validateField('chave')"
        />
        <span v-if="errors.chave" class="error-text" data-testid="translations-form-error">
          {{ errors.chave }}
        </span>
      </div>

      <p class="hint">
        {{ t('A chave É o texto em português que aparece no código. As demais traduções são geradas automaticamente.') }}
      </p>
    </template>

    <div class="form-actions">
      <button
        type="submit"
        class="btn btn-primary"
        :disabled="loading"
        data-testid="translations-form-submit"
      >
        <span v-if="loading" class="spinner"></span>
        {{ isEditMode ? t('Salvar') : t('Criar chave') }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  useTranslationsAdmin,
  TEXTO_VAZIO,
} from '../../composables/useTranslationsAdmin'
import { log } from '../../utils/DebugLogger'
import SelectList from '../../inputs/SelectList.vue'
import InputText from '../../inputs/InputText.vue'
import type { Traducoes_ItemRow } from '../../contracts/Traducoes_ItemRow'
import type { Traducoes_PaginaRow } from '../../contracts/Traducoes_PaginaRow'

const { t } = useI18n()
const composable = useTranslationsAdmin()

const uid = Math.random().toString(36).slice(2, 11)

const emit = defineEmits<{
  'save-success': []
}>()

const props = withDefaults(
  defineProps<{
    paginas?: Traducoes_PaginaRow[]
    item?: Traducoes_ItemRow | null
    idioma?: string
    pagina?: string
  }>(),
  { paginas: () => [], item: null, idioma: '', pagina: '' },
)

const isEditMode = computed<boolean>(() => !!props.item)

const formData = ref({ pagina: '', chave: '', texto: '' })
const errors = ref<{ pagina: string | null; chave: string | null; texto: string | null }>({
  pagina: null,
  chave: null,
  texto: null,
})
const loading = ref<boolean>(false)

const pageOptions = computed(() =>
  props.paginas.map((p) => ({ ID: p.nome ?? '', Name: p.nome ?? '' })),
)

watch(
  () => [props.item, props.pagina, props.idioma] as const,
  () => {
    formData.value.pagina = props.item?.pagina ?? props.pagina ?? ''
    formData.value.chave = props.item?.chave ?? ''
    formData.value.texto = props.item?.traducoes?.[props.idioma] ?? ''
    errors.value = { pagina: null, chave: null, texto: null }
  },
  { immediate: true },
)

const validateField = (field: 'pagina' | 'chave'): void => {
  if (field === 'pagina') {
    errors.value.pagina = formData.value.pagina ? null : t('Página é obrigatória')
  }
  if (field === 'chave') {
    errors.value.chave = formData.value.chave?.trim() ? null : t('Valor padrão é obrigatório')
  }
}

const validateForm = (): boolean => {
  validateField('pagina')
  validateField('chave')
  return !errors.value.pagina && !errors.value.chave
}

/**
 * The proxy refuses a blank text with 422 `texto_vazio_nao_limpa_traducao`
 * because upstream would answer success without writing anything. Render that
 * refusal next to the field rather than as a toast: it is a correction the
 * translator has to make right here.
 */
const mensagemDeRefusa = (codigo: string): string =>
  codigo === TEXTO_VAZIO
    ? t('A tradução não pode ficar em branco — o serviço não apaga traduções.')
    : codigo

const handleSubmit = async (): Promise<void> => {
  log('TranslationsForm', 'handleSubmit', 'Enviando', { isEditMode: isEditMode.value })
  errors.value.texto = null
  loading.value = true

  try {
    if (isEditMode.value && props.item) {
      await composable.salvar({
        pagina: props.item.pagina,
        chave: props.item.chave,
        idioma: props.idioma,
        texto: formData.value.texto,
      })
      emit('save-success')
      return
    }

    if (!validateForm()) {
      log('TranslationsForm', 'handleSubmit', 'Validação falhou')
      return
    }

    await composable.criarChave({
      pagina: formData.value.pagina,
      chave: formData.value.chave.trim(),
    })

    formData.value = { pagina: props.pagina ?? '', chave: '', texto: '' }
    emit('save-success')
  } catch (err) {
    const codigo = err instanceof Error ? err.message : String(err)
    log('TranslationsForm', 'handleSubmit', 'Erro', codigo)
    if (isEditMode.value) {
      errors.value.texto = mensagemDeRefusa(codigo)
    } else {
      errors.value.chave = mensagemDeRefusa(codigo)
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.translations-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.form-readonly {
  padding: 8px 10px;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  color: #555;
}

.error-text {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.hint {
  color: #666;
  font-size: 12px;
  font-style: italic;
  margin: 0;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  justify-content: flex-end;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #0066cc;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0052a3;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
